<!-- The dashboard NAME and the settings gear (Edit Layout / Reset / Back to
     Builder / Share) now live in the shared TOP toolbar — pushed there via
     DashboardTitleService + PublishedDashboardToolbarService — so this view
     renders NO in-page header row at all. Only while editing does a small
     floating Cancel / Save pill appear (top-right), so the primary action is
     always reachable without hunting in the top-bar menu mid-edit. -->

<!-- Reusable "render this widget's actual chart/control" block — used for a
     normal top-level tile AND for each child inside a group-card, so grouping
     never needs a second copy of the per-type render logic. -->
<ng-template #widgetBody let-rec="rec">
  @switch (rec.widget?.type) {
    @case ('waterfall-chart') {
      <header class="waterfall-card-header" [title]="(rec.widget?.title || rec.id) | capitalCase">
        <h4>{{ (rec.widget?.title || rec.id) | capitalCase }}</h4>
      </header>
      <app-waterfall-chart class="dvm-rendered-chart waterfall-chart-body" [data]="rec.widget" (crossFilterChange)="onPublishedCrossFilter(rec, $event)"></app-waterfall-chart>
    }
    @case ('drill-down') {
      @if (rec.widget?.title) {
        <header class="chart-card-header" [title]="rec.widget?.title | capitalCase">
          <h4>{{ rec.widget?.title | capitalCase }}</h4>
        </header>
      }
      <lib-drill-down [data]="rec.widget"></lib-drill-down>
    }
    @case ('table-matrix') {
      @if (rec.widget?.title || rec.widget?.toggleView?.enabled) {
        <header class="chart-card-header" [class.has-header-actions]="rec.widget?.toggleView?.enabled" [title]="rec.widget?.title | capitalCase">
          <h4>{{ rec.widget?.title | capitalCase }}</h4>
          <!-- View toggle sits in the tile header, on the right of the title.
               Visibility + labels come from the widget config (always present),
               so nothing here depends on the table component having rendered
               yet; only the click and the active state read `tmChart`. -->
          @if (rec.widget?.toggleView?.enabled) {
            <div class="chart-card-header-actions">
              <!-- Loading renders on the toggle itself and the error slot keeps
                   its width, so switching views never reflows the header. -->
              <div class="tm-view-toggle" role="group" aria-label="Table view"
                [class.is-loading]="!!tmChart?.toggleViewLoading"
                [attr.aria-busy]="!!tmChart?.toggleViewLoading">
                <button type="button" class="tm-btn"
                  [class.tm-btn-active]="(tmChart?.activeToggleView ?? 1) === 1"
                  [disabled]="!!tmChart?.toggleViewLoading"
                  (click)="tmChart.setToggleView(1)"
                >{{ rec.widget?.toggleView?.labels?.[0] || 'View 1' }}</button>
                <button type="button" class="tm-btn"
                  [class.tm-btn-active]="(tmChart?.activeToggleView ?? 1) === 2"
                  [disabled]="!!tmChart?.toggleViewLoading"
                  (click)="tmChart.setToggleView(2)"
                >{{ rec.widget?.toggleView?.labels?.[1] || 'View 2' }}</button>
              </div>
              <span class="tm-view-toggle-error" role="alert"
                [class.is-visible]="!tmChart?.toggleViewLoading && !!tmChart?.toggleViewError"
                [title]="tmChart?.toggleViewError">⚠</span>
            </div>
          }
        </header>
      }
      <app-unified-table-matrix-chart
        #tmChart
        [data]="rec.widget"
        [hostRendersViewToggle]="true"
        (navigate)="onTableNavigate($event)"
        (refresh)="onTableRefresh(rec)"
        (saved)="onTableSaved(rec)"
        (columnOrderChange)="onTableColumnReorder(rec, $event)"
        (save)="onTableSave(rec, $event)"
        (workflow)="onTableWorkflow(rec, $event)"
        (cellAction)="onTableCellAction(rec, $event)"
      ></app-unified-table-matrix-chart>
    }
    @case ('horizontal-bar-chart') {
      <app-horizontal-bar-chart [data]="rec.widget"></app-horizontal-bar-chart>
    }
    @case ('bar-column') {
      @if (rec.widget?.title) {
        <header class="chart-card-header" [title]="rec.widget?.title | capitalCase">
          <h4>{{ rec.widget?.title | capitalCase }}</h4>
        </header>
      }
      <app-unified-bar-column-chart class="dvm-rendered-chart bar-column-chart-body" [data]="rec.widget" (crossFilterChange)="onPublishedCrossFilter(rec, $event)"></app-unified-bar-column-chart>
    }
    @case ('line-area') {
      @if (rec.widget?.title) {
        <header class="chart-card-header" [title]="rec.widget?.title | capitalCase">
          <h4>{{ rec.widget?.title | capitalCase }}</h4>
        </header>
      }
      <app-unified-line-area-chart class="dvm-rendered-chart bar-column-chart-body" [data]="rec.widget" (crossFilterChange)="onPublishedCrossFilter(rec, $event)"></app-unified-line-area-chart>
    }
    @case ('scatter-bubble') {
      @if (rec.widget?.title) {
        <header class="chart-card-header" [title]="rec.widget?.title | capitalCase">
          <h4>{{ rec.widget?.title | capitalCase }}</h4>
        </header>
      }
      <app-unified-scatter-bubble-chart class="dvm-rendered-chart bar-column-chart-body" [data]="rec.widget" (crossFilterChange)="onPublishedCrossFilter(rec, $event)"></app-unified-scatter-bubble-chart>
    }
    @case ('kpi-card') {
      <app-number-stats-shared [data]="rec.widget"></app-number-stats-shared>
    }
    @case ('slider') {
      <div class="dvm-input-shell">
        <app-mat-slider [data]="rec.widget" (valueChange)="onInputWidgetChange(rec.widget, $event)"></app-mat-slider>
      </div>
    }
    @case ('input-box') {
      <div class="dvm-input-shell">
        <app-input-box [data]="rec.widget" (valueChange)="onInputWidgetChange(rec.widget, $event)"></app-input-box>
      </div>
    }
    @case ('dropdown') {
      <div class="dvm-input-shell">
        <app-global-dropdown
          [data]="rec.widget"
          [options]="rec.widget?.optionsList || []"
          [ngModel]="rec.widget?.payload?.value || []"
          (ngModelChange)="onInputWidgetChange(rec.widget, $event)"
        ></app-global-dropdown>
      </div>
    }
    @case ('file-upload') {
      <div class="dvm-input-shell dvm-file-upload-shell">
        <app-dvm-file-upload
          [title]="rec.widget?.title || 'File Upload'"
          [buttonLabel]="rec.widget?.buttonLabel || 'Upload file'"
          [accept]="rec.widget?.accept || '.xlsx,.xls,.csv'"
          [uploading]="isFileUploading(rec.widget)"
          [message]="fileUploadMessage(rec.widget)"
          (filePicked)="onFilePicked(rec.widget, $event)"
        ></app-dvm-file-upload>
      </div>
    }
    @case ('popup') {
      <!-- No card header: a popup tile IS its trigger button (which already
           carries the label), so a title bar above it just repeated the text
           and boxed the button in. Bare, like the dropdown controls. -->
      <div class="dvm-input-shell dvm-popup-shell">
        <app-dvm-popup [data]="rec.widget" (navigateTo)="onPopupNavigate($event)"></app-dvm-popup>
      </div>
    }
    @default {
      <div class="unsupported-widget">Unsupported widget type: {{ rec.widget?.type }}</div>
    }
  }
</ng-template>

@if (editMode) {
  <div class="dvm-published-actionbar is-editing">
    <button type="button" class="secondary-action" (click)="cancelEditMode()">Cancel</button>
    <button type="button" class="primary-action" (click)="saveLayout()">Save Layout</button>
  </div>
}

<!-- Shell: the dashboard column + the optional "Available Views" rail on the
     right (the viewer's other published dashboards as preview cards). -->
<div class="dvm-published-shell" [class.has-rail]="showViewsRail">
<div class="dvm-published-shell-main">
@if (loading) {
  <div class="dvm-published-state">Loading dashboard...</div>
} @else if (loadError) {
  <div class="dvm-published-state error">{{ loadError }}</div>
} @else if (landingConfig) {
  <!-- Landing Page template — rendered whole by the landing component; no
       widget grid, edit-layout, or recompute machinery applies. -->
  <app-dvm-landing-page [config]="landingConfig" [layoutEdit]="editMode"></app-dvm-landing-page>
} @else {
  <!-- ONE dashboard-level progress bar for any in-flight recompute — the single
       obvious "working" signal, so per-tile indicators can stay whisper-quiet. -->
  @if (isAnyWidgetBusy) {
    <div class="dvm-global-busy-bar" role="status" aria-label="Updating dashboard"></div>
  }

  <main class="dvm-published-canvas" [class.editing]="editMode" (dragover)="onCanvasDragOver($event)" (drop)="onCanvasDrop($event)">
    <!-- Filter-rail backdrop: one tinted panel behind a stacked column of
         standalone dropdown tiles (view mode only, see filterRailBackdrops).
         Rendered FIRST so it paints beneath the tiles in tree order. -->
    @for (rail of filterRailBackdrops; track rail.key) {
      <div class="dvm-filter-rail-backdrop" aria-hidden="true" [style.grid-column]="rail.gridColumn" [style.grid-row]="rail.gridRow"></div>
    }
    @for (entry of viewItems; track entry.renderKey) {
      <article
        class="dvm-published-tile"
        [class.busy]="entry.rec.status === 'loading' || isWidgetBusy(entry.rec.widget)"
        [class.failed]="entry.rec.status === 'error'"
        [class.kpi-tile]="entry.rec.widget?.type === 'kpi-card'"
        [class.slider-tile]="entry.rec.widget?.type === 'slider'"
        [class.dropdown-tile]="entry.rec.widget?.type === 'dropdown'"
        [class.dropdown-bare]="isCompactDropdownTile(entry.rec.widget)"
        [class.rail-member]="railRole(entry.item.id) !== null"
        [class.rail-first]="railRole(entry.item.id) === 'first'"
        [class.rail-last]="railRole(entry.item.id) === 'last'"
        [class.file-upload-tile]="entry.rec.widget?.type === 'file-upload'"
        [class.popup-tile]="entry.rec.widget?.type === 'popup'"
        [class.waterfall-tile]="entry.rec.widget?.type === 'waterfall-chart'"
        [class.bar-column-tile]="entry.rec.widget?.type === 'bar-column' || entry.rec.widget?.type === 'scatter-bubble' || entry.rec.widget?.type === 'line-area'"
        [class.chart-header-tile]="entry.rec.widget?.type === 'drill-down' || entry.rec.widget?.type === 'table-matrix' || entry.rec.widget?.type === 'horizontal-bar-chart'"
        [class.filter-rail-tile]="isFilterGroupCard(entry.rec.widget)"
        [class.fit-content-tile]="(entry.rec.widget?.type === 'drill-down' || entry.rec.widget?.type === 'table-matrix') && entry.rec.status !== 'loading' && !editMode"
        [style.--dvm-card-header-bg]="takesHeaderFormat(entry.rec.widget) ? (entry.rec.widget?.format?.cardHeaderBackground || null) : null"
        [style.--dvm-card-header-color]="takesHeaderFormat(entry.rec.widget) ? (entry.rec.widget?.format?.cardHeaderColor || null) : null"
        [style.--dvm-card-header-align]="takesHeaderFormat(entry.rec.widget) ? (entry.rec.widget?.format?.titleAlign || null) : null"
        [style.--dvm-tile-font-family]="tileFontFamily(entry.rec.widget?.format)"
        [style.--dvm-tile-font-size]="tileFontSizePx(entry.rec.widget?.format)"
        [ngStyle]="groupCardStyles(entry.rec.widget)"
        [attr.data-tile-id]="entry.item.id"
        [style.grid-column]="tileGridColumn(entry)"
        [style.grid-row]="tileGridRow(entry)"
        (dragover)="onTileDragOver($event)"
        (drop)="onTileDrop(entry.item.id, $event)"
      >
        <!-- FIRST load has no content to show yet, so a centred spinner is the
             only option. A RECOMPUTE (input/slider change) already has readable
             numbers on screen — veiling every targeted tile at once made the
             whole dashboard strobe, so those keep their content and get a slim
             progress line at the tile's top edge instead. -->
        @if (entry.rec.status === 'loading') {
          <div class="tile-skeleton" [attr.data-variant]="skeletonVariant(entry.rec.widget)" role="status" aria-label="Loading">
            <span class="sk-block"></span>
            <span class="sk-line"></span>
            <span class="sk-line"></span>
            <span class="sk-line"></span>
            <span class="sk-line"></span>
          </div>
        } @else if (isWidgetBusy(entry.rec.widget)) {
          <span class="tile-busy-bar" aria-hidden="true"></span>
        }

        @if (editMode) {
          <header
            class="tile-edit-bar"
            draggable="true"
            (dragstart)="onTileDragStart(entry.item.id, $event)"
            (dragend)="onTileDragEnd()"
            matTooltip="Drag to reorder"
          >
            <span class="tile-drag-grip" aria-hidden="true">⋮⋮</span>
          </header>
        }

        <div class="tile-body" [class.group-card-body]="entry.rec.widget?.type === 'group-card'">
          @if (entry.rec.widget?.type === 'group-card') {
            @if (showGroupCardHeader(entry.rec.widget)) {
              <header class="group-card-header" [title]="entry.rec.widget?.title || entry.item.id">
                <h4>{{ entry.rec.widget?.title || entry.item.id }}</h4>
              </header>
            }
            <div class="group-card-members" [class.two-col]="(entry.rec.widget?.groupColumns || 1) >= 2">
              @for (childId of entry.rec.widget?.childWidgetIds || []; track childId) {
                @if (widgetRecById(childId); as childRec) {
                  <div
                    class="group-card-member"
                    [class.compact-member]="isCompactGroupMember(childRec.widget)"
                    [style.height.px]="memberHeightFor(entry.rec.widget, childId)"
                    [style.minHeight]="memberHeightFor(entry.rec.widget, childId) ? '0' : null"
                    [style.flex]="memberHeightFor(entry.rec.widget, childId) ? '0 0 auto' : null"
                    [style.--dvm-card-header-align]="childRec.widget?.format?.titleAlign || null"
                  >
                    <span class="group-card-member-title" [title]="childRec.widget?.title">{{ childRec.widget?.title }}</span>
                    <div class="group-card-member-body">
                      <ng-container *ngTemplateOutlet="widgetBody; context: { rec: childRec }"></ng-container>
                    </div>
                    @if (editMode) {
                      <span class="member-resize-handle" (mousedown)="startMemberResize(entry.rec, childId, $event)" matTooltip="Drag to set this widget's height" aria-hidden="true"></span>
                    }
                  </div>
                }
              }
            </div>
          } @else {
            <ng-container *ngTemplateOutlet="widgetBody; context: { rec: entry.rec }"></ng-container>
          }
        </div>

        @if (entry.rec.error) {
          <div class="tile-warning" [matTooltip]="entry.rec.error" [attr.aria-label]="entry.rec.error">{{ entry.rec.error }}</div>
        }

        @if (editMode) {
          <span class="tile-resize-handle edge top" (mousedown)="startTileResize(entry.item.id, $event, null, 'top')" matTooltip="Resize from top" aria-hidden="true"></span>
          <span class="tile-resize-handle edge right" (mousedown)="startTileResize(entry.item.id, $event, 'right', null)" matTooltip="Resize from right" aria-hidden="true"></span>
          <span class="tile-resize-handle edge bottom" (mousedown)="startTileResize(entry.item.id, $event, null, 'bottom')" matTooltip="Resize from bottom" aria-hidden="true"></span>
          <span class="tile-resize-handle edge left" (mousedown)="startTileResize(entry.item.id, $event, 'left', null)" matTooltip="Resize from left" aria-hidden="true"></span>
          <span class="tile-resize-handle corner top-left" (mousedown)="startTileResize(entry.item.id, $event, 'left', 'top')" matTooltip="Resize" aria-hidden="true"></span>
          <span class="tile-resize-handle corner top-right" (mousedown)="startTileResize(entry.item.id, $event, 'right', 'top')" matTooltip="Resize" aria-hidden="true"></span>
          <span class="tile-resize-handle corner bottom-left" (mousedown)="startTileResize(entry.item.id, $event, 'left', 'bottom')" matTooltip="Resize" aria-hidden="true"></span>
          <span class="tile-resize-handle corner bottom-right" (mousedown)="startTileResize(entry.item.id, $event, 'right', 'bottom')" matTooltip="Resize" aria-hidden="true"></span>
        }
      </article>
    }
  </main>
}
</div>
@if (showViewsRail) {
  <app-dvm-available-views-rail
    [views]="availableViews"
    [currentSlug]="slug"
    (open)="onOpenAvailableView($event)"
  ></app-dvm-available-views-rail>
}
</div>






